import { Injectable } from "@angular/core";
import { HttpEventType } from "@angular/common/http";
import { Observable, of, throwError, map, last, catchError, switchMap, forkJoin, tap } from "rxjs";
import { FileDownload, FileDownloadStatus, FileDownloadBinary, FileDownloadAdapter } from "@app/shared/models/file.model";
import { IPLUS_URL } from "@app/shared/constants/url.constants";
import { FileTypes } from "@app/shared/constants/file-types.constants";
import { FileService } from "@app/core/services/file.service";

@Injectable({
  providedIn: "root"
})
export class FileDownloadService extends FileService {
  /**
   * Download a file from the server and return a binary file
   * @param file
   * @param progressHandler
   */
  downloadFile(
    file: FileDownload,
    progressHandler?: (status: FileDownloadStatus) => void,
    skipLoader: boolean = true
  ): Observable<FileDownloadBinary> {
    return this._download(file, progressHandler, skipLoader);
  }

  /**
   * Download a file from server and return data as string content.
   * @param file
   * @param progressHandler
   */
  downloadFileAsString(file: FileDownload, progressHandler?: (status: FileDownloadStatus) => void, skipLoader: boolean = true): Observable<string> {
    return this._download(file, progressHandler, skipLoader)
      .pipe(switchMap((result) => this._readFileAsText(result.binaryFile)))
      .pipe(
        map((result) => {
          return result ? result : "";
        })
      )
      .pipe(catchError(() => of("")));
  }

  /**
   * Download a file from server and convert to a Base64 encoded file.
   * @param file
   * @param progressHandler
   */
  downloadFileAsBase64(file: FileDownload, progressHandler?: (status: FileDownloadStatus) => void, skipLoader: boolean = true): Observable<string> {
    return this._download(file, progressHandler, skipLoader)
      .pipe(switchMap((result) => this.readFileAsDataUrl(result.binaryFile)))
      .pipe(map((result) => result.split(",")[1]))
      .pipe(
        map((result) => {
          return result ? result : "";
        })
      )
      .pipe(catchError(() => of("")));
  }

  /**
   * Fetch help documents from the assets folder based on the path
   * and return the content
   * @param path
   */
  downloadHelpFileAsBase64(file: FileDownload, skipLoader: boolean = true): Observable<string> {
    return this.http
      .removeDefaultHeaders()
      .removeDefaultEncoding()
      .makeNonUSSICall()
      .skipLoaderHandler(skipLoader)
      .get(file.file, { responseType: "text" });
  }

  /**
   * Download an image from server and convert to a Base64 encoded url string.
   * @param file
   * @param progressHandler
   * @param skipLoader
   */
  downloadFileAsBase64Url(
    file: FileDownload,
    progressHandler?: (status: FileDownloadStatus) => void,
    skipLoader: boolean = false
  ): Observable<string> {
    return this._download(file, progressHandler, skipLoader)
      .pipe(switchMap((result) => forkJoin({ url: this.readFileAsDataUrl(result.binaryFile), type: of(result.fileType) })))
      .pipe(
        map((result) => {
          if (!result?.url || !result?.type) {
            return "";
          }

          result.url = result.url.split(",")[1];

          const isBase64: string = this.checkForBase64(result.url);
          if (isBase64) {
            return isBase64;
          }

          const mimeType = this._handleMimeType(file.file, result?.type?.mime);
          return mimeType ? `data:${mimeType};base64,${result.url}` : "";
        })
      )
      .pipe(catchError(() => of("")));
  }

  /**
   * Download and save file
   * @param file
   */
  downloadAndSave(file: FileDownload, skipLoader?: boolean, progressHandler?: (status: FileDownloadStatus) => void) {
    this.downloadFile(file, progressHandler, skipLoader).subscribe({
      next: (resp) => {
        this.saveDownloadedFileAs(resp.binaryFile, this.getDocumentName(file.file), true);
      },
      error: () => {}
    });
  }

  /**
   * Read the contents of a Blob using the FileReader interface.
   * Implement as an observable in order to integrate with Rxjs.
   * .readAsDataURL() return a URL representing the file's data as a base64 encoded string
   * @param blob
   * @return Observable<string>
   */
  readFileAsDataUrl = (blob: Blob): Observable<string> => {
    if (!(blob instanceof Blob)) {
      return throwError(() => new Error("`blob` must be an instance of Blob."));
    }

    return new Observable((obs: any) => {
      const reader = new FileReader();
      reader.onerror = (err) => obs.error(err);
      reader.onabort = (err) => obs.error(err);
      reader.onload = () => obs.next(reader.result);
      reader.onloadend = () => obs.complete();
      return reader.readAsDataURL(blob);
    });
  };

  /**
   * Download a file from server
   * @param file
   * @param progressHandler
   */
  private _download(
    file: FileDownload,
    progressHandler?: (status: FileDownloadStatus) => void,
    skipLoader?: boolean
  ): Observable<FileDownloadBinary> {
    let value: FileDownloadBinary = { binaryFile: null, error: false, fileType: undefined };
    const adapter: FileDownloadAdapter = new FileDownloadAdapter();

    // Don't call web service if no data provided.
    if (!file?.file || file?.file?.trim()?.length === 0) {
      return of(value);
    }

    return this.http
      .addDataParams(file)
      .makeDownloadFileCall()
      .addServiceWorkerBypass()
      .skipLoaderHandler(skipLoader)
      .get(IPLUS_URL.DOWNLOAD_FILE, {
        reportProgress: true,
        observe: "events",
        responseType: "blob"
      })
      .pipe(
        map((event) => {
          switch (event.type) {
            case HttpEventType.DownloadProgress:
              if (progressHandler) {
                progressHandler({ status: "progress", loaded: event.loaded });
              }
              return {};

            case HttpEventType.Response:
              if (progressHandler) {
                progressHandler({ status: "completed", loaded: event.body.size });
              }
              return event.body;

            default:
              return {};
          }
        })
      )
      .pipe(
        last(),
        map((data: any) => adapter.adapt(data)),
        switchMap((data) => {
          value = { ...data };
          return this.getFileTypeFromBlob(value.binaryFile);
        }),
        tap((data) => {
          value.fileType = data[0];
        }),
        map(() => value),
        catchError(() => {
          value.error = true;
          return of(value);
        })
      );
  }

  /**
   * Read the contents of a Blob using the FileReader interface.
   * Implement as an observable in order to integrate with Rxjs.
   * .readAsText() return the file's data as a text string.
   * @param blob
   * @return Observable<string>
   */
  private _readFileAsText = (blob: Blob): Observable<string> => {
    if (!(blob instanceof Blob)) {
      return throwError(() => new Error("`blob` must be an instance of Blob."));
    }

    return new Observable((obs: any) => {
      const reader = new FileReader();
      reader.onerror = (err) => obs.error(err);
      reader.onabort = (err) => obs.error(err);
      reader.onload = () => obs.next(reader.result);
      reader.onloadend = () => obs.complete();
      return reader.readAsText(blob);
    });
  };

  /**
   * file-type package does not handle text based files, set proper type based on extension.
   * @param file
   * @param mimeType
   * @returns
   */
  private _handleMimeType(file: string, mimeType: string) {
    const ext = this.getDocumentType(file);
    switch (ext) {
      // Render images as next gen format
      case FileTypes.PNG:
      case FileTypes.JPEG:
      case FileTypes.JPG: {
        return "image/webp";
      }
      case FileTypes.SVG: {
        return "image/svg+xml";
      }
      case FileTypes.TEXT: {
        return "text/plain";
      }
      case FileTypes.CSV: {
        return "text/csv";
      }
      case FileTypes.DOC: {
        return "application/msword";
      }
      case FileTypes.XLSX: {
        return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      }
      case FileTypes.XLS: {
        return "application/vnd.ms-excel";
      }
      default: {
        return mimeType;
      }
    }
  }
}
